import _ from 'lodash';
import moment from 'moment';
import { ILumaBotFlowQuestion, ILumaBotTemplateItemAnswer, TPatient } from 'ui-model';
import { Address } from 'ui-utils';
import { v4 as uuid } from 'uuid';

import { Language } from '@lib/resources/types';

import { TMessageWrapper } from './components/MessageWrapper';
import { EMessageType, EUserContactType, IUserContact } from './WIP/ui-model/types';

type TAddress = {
  country?: string;
  address?: string;
  city?: string;
  state?: string;
  postcode?: string;
  value?: string;
};

export type TLumaBotAnswers = Record<string, string | TAddress>;

function updateText(text: any, replacements: any) {
  let res = text;
  _.forEach(replacements, ({ prev, next }) => {
    res = _.replace(res, prev, next);
  });
  return res;
}

export const sanitizeLumaBotMessage = (
  partial: Partial<TMessageWrapper>,
  language: Language,
  botName: string,
  answers?: TLumaBotAnswers,
): TMessageWrapper => {
  const squigglyReplacements = _.map(answers, (next, prev) => ({ prev: `{{${prev}}}`, next }));
  let createdBy: any = partial.createdBy;
  if (partial.type !== EMessageType.Outbound) {
    if (partial.user) {
      createdBy = {
        type: 'staff',
        // TODO: Waiting for product decision:
        // Use staff name (which would require an additional fetch per message),
        // or set a default name/avatar for staff users
        name: 'Staff',
      };
    } else {
      createdBy = {
        type: 'bot',
        name: botName,
      };
    }
  }
  const message = {
    ...partial,
    _id: partial._id || uuid(),
    createdAt: partial.createdAt || moment().format(),
    createdBy,
  };

  const { text: initialText, metadata = {} } = message;
  let text = initialText || '';
  if (message.type === 'inbound' && metadata.item && metadata.item.type === 'template') {
    const textTranslation = updateText(getTranslation(metadata.item, language, 'title') || text, squigglyReplacements);
    const templateTranslation = updateText(getTranslation(metadata.item, language, 'template'), squigglyReplacements);

    _.set(message, 'text', textTranslation);
    _.set(message, 'template', templateTranslation);

    return message as TMessageWrapper;
  }
  if (metadata.item && message.type !== EMessageType.Outbound && !isMessageTemplateKey(text)) {
    text = updateText(getTranslation(metadata.item, language, 'title') || text, squigglyReplacements);
  }
  if (metadata.answer) {
    text = updateText(getTranslation(metadata.answer, language, 'label') || text, squigglyReplacements);
  }

  return { ...message, text } as TMessageWrapper;
};

export function sanitizeLumaBotMessages(
  messages: Partial<TMessageWrapper>[],
  language: Language,
  botName = '',
  currentAnswers?: TLumaBotAnswers,
  loading?: boolean,
  typing?: boolean,
) {
  const result: TMessageWrapper[] = _.map(messages, (message, index, collection) => {
    const nextMessage: any = collection[index + 1];
    const prevMessage: any = collection[index - 1];

    return {
      ...sanitizeLumaBotMessage(message, language, botName, currentAnswers),
      metadata: {
        ...message.metadata,
        isLastMessage: index === messages.length - 1,
        nextMessage: nextMessage && sanitizeLumaBotMessage(nextMessage, language, botName, currentAnswers),
        prevMessage: prevMessage && sanitizeLumaBotMessage(prevMessage, language, botName, currentAnswers),
      },
    };
  });
  // Return button should only be available for the last outbound message
  const lastOutboundMsg: any = _.findLast(result, { type: EMessageType.Outbound });
  const lastInboundMsg: any = _.findLast(result, { type: EMessageType.Inbound });

  const shouldRevertBeDisabled = loading || typing || (lastInboundMsg && lastInboundMsg.user);
  if (lastOutboundMsg && !_.has(lastOutboundMsg.metadata, 'includeRevert') && !shouldRevertBeDisabled) {
    lastOutboundMsg.metadata.includeRevert = true;
  }

  return result;
}

export function getTranslation(
  item: ILumaBotFlowQuestion | ILumaBotTemplateItemAnswer,
  language: Language,
  fieldName:
    | 'title'
    | 'description'
    | 'label'
    | 'schedulerButton'
    | 'template'
    | 'properties.actionLabel'
    | 'properties.actionHref',
) {
  // NOTE: We can't rename `locales` to `languages` b/c we need to support
  // forms that are currently stored on the DB
  return (
    _.get(item, `locales.${language}.${fieldName}`) ||
    _.get(item, `properties.locales.${language}.${fieldName}`) ||
    _.get(item, fieldName) ||
    _.get(item, `properties.${fieldName}`)
  );
}

const MESSAGE_TEMPLATE_PREFIX = '%message_template%';

/**
 * Generate text with a message template key
 * @param id
 * @param type
 * @example
 * const text = generateMessageTemplateKey('patientLumaBotWelcome');
 * // text === '%message_template%messages.web.patientLumaBotWelcome'
 */
export const generateMessageTemplateKey = (id: string, type: 'web' | 'inapp' = 'web') =>
  `${MESSAGE_TEMPLATE_PREFIX}messages.${type}.${id}`;

export const isMessageTemplateKey = (text?: string) => text && text.indexOf(MESSAGE_TEMPLATE_PREFIX) === 0;

/**
 * Returns the message template key from a text
 * @param text
 * @example
 * const templateKey = getMessageTemplateKey('%message_template%messages.web.patientLumaBotWelcome');
 * // templateKey === 'messages.web.patientLumaBotWelcome'
 */
export const getMessageTemplateKey = (text: string) => {
  if (isMessageTemplateKey(text)) {
    return text.replace(MESSAGE_TEMPLATE_PREFIX, '');
  }
  return '';
};

export const getLumaBotPatientData = (answers: TLumaBotAnswers) => {
  const patient: Partial<TPatient> = {
    firstname: answers['patient.firstName'] as string,
    lastname: answers['patient.lastName'] as string,
    ..._.omit(answers['patient.address'] as Address, ['value']),
    // @ts-ignore
    widget: 'lumabot',
  };

  // Sanitize dateOfBirth
  const dateOfBirth = answers['patient.dob'] as string;
  let dob: string[] = [];
  if (/[-]/.test(dateOfBirth)) {
    dob = dateOfBirth.split('-');
  } else if (/[/]/.test(dateOfBirth)) {
    dob = dateOfBirth.split('/');
  }
  // @ts-ignore
  patient.dateOfBirth = { month: dob[0], day: dob[1], year: dob[2] };

  // Sanitize contact
  const contact: IUserContact[] = [];
  const phone = answers['patient.phone'] as string;
  if (phone) {
    contact.push({
      type: EUserContactType.Sms,
      value: `${phone}`.replace(/[()-\s]/g, ''),
      active: true,
    });
  }
  const email = answers['patient.email'] as string;
  if (email) {
    contact.push({
      type: EUserContactType.Email,
      value: email,
      active: !phone,
    });
  }
  patient.contact = contact as any;

  return patient;
};

export const getErrorTitle = (itemType?: string) => {
  if (itemType === 'scheduler') return generateMessageTemplateKey('patientLumaBotSchedulerError');
  if (itemType === 'manageAppointment') return generateMessageTemplateKey('patientLumaBotManageAppointmentError');
  return 'Unfortunately something went wrong, please try again later.';
};
